<!--
  Replacement for: src/submodules/carbon-plugin/components/dashboard/components/DashboardKpiRow.vue

  Props are backwards-compatible — keeps `summary`, `yearOverYear`, `displayUnit`
  and adds optional `target` (RAW.target widget) + `prevTrend` (월별 전년 실적 배열).
  If `target` / `prevTrend` is null, the two new peer blocks gracefully hide.
-->
<template>
    <div class="kpi-hero">
        <!-- Col 1: YTD total + narrative + year progress -->
        <div class="kpi-col kpi-col--first">
            <div class="kpi-eyebrow">
                <span>{{ periodTitle }}</span>
                <span class="kpi-eyebrow-dash"></span>
                <span class="kpi-eyebrow-faint">{{ cutoffLabel }} 기준</span>
                <span v-if="target?.isDraft" class="kpi-pill kpi-pill--draft">잠정</span>
            </div>
            <div class="kpi-big-num num">
                <span>{{ formatN(summary.totalEmission, 2) }}</span>
                <span class="kpi-unit">{{ unitLabel }}</span>
            </div>
            <!-- YoY compact row -->
            <div class="ksi-yoy" v-if="yoyChangeRate != null">
                <span class="ksi-yoy-label">{{ primaryCompareLabel }}</span>
                <span :class="['ksi-yoy-val', 'num', yoyChangeRate < 0 ? 'kpi-delta--good' : 'kpi-delta--bad']">
                    {{ neg(yoyChangeRate, "▼", "▲") }} {{ Math.abs(yoyChangeRate).toFixed(1) }}%
                </span>
                <span class="ksi-yoy-abs"
                    >( {{ formatN(Math.abs(prevYtd - currentYtd), 0) }} {{ unitShort }} 차이 )</span
                >
            </div>

            <!-- Scope 인라인 시각화 -->
            <div class="ksi-wrap">
                <div class="ksi-bar">
                    <div
                        v-for="s in scopes"
                        :key="s.key"
                        class="ksi-seg"
                        :style="{ flex: s.val || 1, background: s.color, opacity: s.val === 0 ? 0.25 : 1 }"
                    ></div>
                </div>
                <div class="ksi-items">
                    <div v-for="s in scopes" :key="s.key" class="ksi-item">
                        <span class="kpi-dot" :style="{ background: s.color, opacity: s.val === 0 ? 0.3 : 1 }"></span>
                        <span class="ksi-name">{{ s.label }}</span>
                        <span class="ksi-val num">{{ s.val === 0 ? "—" : formatN(s.val, 0) }}</span>
                    </div>
                    <span class="ksi-unit-tag">{{ unitShort }}</span>
                </div>
            </div>

            <!-- year progress bar -->
            <!-- <div class="kpi-yearbar">
                <div class="kpi-yearbar-labels">
                    <span>1월</span>
                    <span class="kpi-yearbar-current">{{ cutoffMonth }}월 {{ cutoffDay }}일 · 진행률 {{ yearProgressPct }}%</span>
                    <span>12월</span>
                </div>
                <div class="kpi-yearbar-track">
                    <div class="kpi-yearbar-fill" :style="{ width: yearProgressPct + '%' }"></div>
                    <div class="kpi-yearbar-marker" :style="{ left: yearProgressPct + '%' }"></div>
                </div>
            </div> -->
            <div class="kpi-divider"></div>
        </div>

        <!-- Col 2: forecast + compliance settlement -->
        <div class="kpi-col">
            <div class="kpi-block" v-if="forecastValue != null">
                <div class="kpi-eyebrow">{{ forecastTitle }}</div>
                <div class="kpi-block-row">
                    <div class="kpi-block-value-wrap">
                        <span class="kpi-block-value num">{{ formatN(forecastValue, 0) }}</span>
                        <span class="kpi-block-unit">{{ unitLabel }}</span>
                    </div>
                    <div v-if="forecastChangeRate != null" class="kpi-block-delta">
                        <div
                            :class="[
                                'kpi-block-delta-value',
                                'num',
                                forecastChangeRate < 0 ? 'kpi-delta--good' : 'kpi-delta--bad',
                            ]"
                        >
                            <span class="kpi-arrow">{{ neg(forecastChangeRate, "▼", "▲") }}</span>
                            {{ Math.abs(forecastChangeRate).toFixed(1) }}%
                        </div>
                        <div class="kpi-block-delta-hint">
                            {{ forecastDeltaHint }} {{ formatN(forecastCompareValue, 0) }} {{ unitShort }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="kpi-block-hr" v-if="forecastValue != null && hasComplianceFlex"></div>

            <div class="kpi-block kpi-block--compliance" v-if="hasComplianceFlex">
                <div class="kpi-eyebrow kpi-eyebrow--wrap">
                    <span>{{ complianceTitle }}</span>
                    <!-- <span :class="['kpi-pill', complianceStatusBadge.warn ? 'kpi-pill--warn' : '']">
                        {{ complianceStatusBadge.label }}
                    </span>
                    <span :class="['kpi-pill', complianceStatBadge.warn ? 'kpi-pill--warn' : 'kpi-pill--mute']">
                        {{ complianceStatBadge.label }}
                    </span> -->
                </div>

                <!-- ① 정산 배출량 → ② 이월·차입·상쇄 반영 → ③ 이번 해 총 배출량 -->
                <div v-if="complianceLinked" class="cpl-flow">
                    <div class="cpl-node cpl-node--gross">
                        <span class="cpl-node-lbl">총 배출량</span>
                        <div class="cpl-node-row">
                            <span class="cpl-node-val num">{{ complianceGrossText }}</span>
                            <span class="cpl-node-unit">{{ unitShort }}</span>
                        </div>
                    </div>

                    <template v-if="settlementEntries.length > 0">
                        <span class="cpl-arrow" aria-hidden="true"></span>
                        <div class="cpl-adjs">
                            <div v-for="e in settlementEntries" :key="e.key" class="cpl-adj">
                                <span class="cpl-adj-tag">{{ e.label }}</span>
                                <span class="cpl-adj-amount">
                                    <span class="cpl-adj-val num">-{{ formatN(e.value, 0) }}</span>
                                    <span class="cpl-adj-unit">{{ unitShort }}</span>
                                </span>
                            </div>
                        </div>
                        <span class="cpl-arrow" aria-hidden="true"></span>
                    </template>

                    <div class="cpl-node cpl-node--net" v-if="netSettlementValue != null">
                        <span class="cpl-node-lbl">정산 배출량</span>
                        <div class="cpl-node-row">
                            <span class="cpl-node-val num">{{ formatN(netSettlementValue, 0) }}</span>
                            <span class="cpl-node-unit">{{ unitShort }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="complianceMessage" class="kpi-compliance-note">{{ complianceMessage }}</div>
            </div>
            <div class="kpi-divider"></div>
        </div>

        <!-- Col 3: 목표 대비 감축률 + 기준연도 대비 감축률 -->
        <div class="kpi-col kpi-col--last">
            <!-- Block 1: 목표 배출량 대비 감축률 -->
            <div class="kpi-block" v-if="target">
                <div class="kpi-eyebrow">
                    <span>연간 목표 대비 감축률</span>
                    <!-- <span :class="['kpi-pill', goalStatBadge.warn ? 'kpi-pill--warn' : '']">{{
                        goalStatBadge.label
                    }}</span> -->
                </div>

                <div v-if="goalAmount && targetCompareValue != null" class="kpi-change-flow">
                    <span class="kpi-flow-node">
                        <span class="kpi-flow-label">목표</span>
                        <span class="num">{{ formatN(goalAmount, 0) }}</span>
                        <span class="kpi-flow-unit">{{ unitShort }}</span>
                    </span>
                    <span class="kpi-flow-arrow" aria-hidden="true"></span>
                    <span class="kpi-flow-node">
                        <span class="kpi-flow-label">{{ targetCompareLabel }}</span>
                        <span class="num">{{ formatN(targetCompareValue, 0) }}</span>
                        <span class="kpi-flow-unit">{{ unitShort }}</span>
                    </span>
                </div>
                <div class="kpi-block-row kpi-block-row--target">
                    <div v-if="targetGapRate != null" class="kpi-block-side-value">
                        <div class="kpi-block-value-wrap kpi-block-value-wrap--side">
                            <span
                                :class="[
                                    'kpi-block-value',
                                    'num',
                                    targetWithinGoal ? 'kpi-delta--good' : 'kpi-block-value--warn',
                                ]"
                            >
                                {{ signedNumber(targetGapRate, 1) }}
                            </span>
                            <span class="kpi-block-unit">%</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="kpi-block-hr" v-if="target"></div>

            <!-- Block 2: 기준연도 대비 감축률 -->
            <div class="kpi-block" v-if="target">
                <div class="kpi-eyebrow">
                    <span>기준연도({{ target.baseYear }}) 대비 감축률</span>
                    <!-- <span :class="['kpi-pill', baselineReduced ? '' : 'kpi-pill--warn']">{{ baselineTrendLabel }}</span> -->
                </div>

                <div v-if="baselineEmission != null && baselineCompareEmission != null" class="kpi-change-flow">
                    <span class="kpi-flow-node">
                        <span class="kpi-flow-label">{{ target.baseYear }}년</span>
                        <span class="num">{{ formatN(baselineEmission, 0) }}</span>
                        <span class="kpi-flow-unit">{{ unitShort }}</span>
                    </span>
                    <span class="kpi-flow-arrow" aria-hidden="true"></span>
                    <span class="kpi-flow-node">
                        <span class="kpi-flow-label">{{ yearText }}년</span>
                        <span class="num">{{ formatN(baselineCompareEmission, 0) }}</span>
                        <span class="kpi-flow-unit">{{ unitShort }}</span>
                    </span>
                </div>
                <div class="kpi-block-row kpi-block-row--target">
                    <div class="kpi-block-side-value">
                        <div class="kpi-block-value-wrap kpi-block-value-wrap--side">
                            <span
                                :class="[
                                    'kpi-block-value',
                                    'num',
                                    baselineReduced ? 'kpi-delta--good' : 'kpi-block-value--warn',
                                ]"
                            >
                                {{ signedNumber(baselineReductionRate, 1) }}
                            </span>
                            <span class="kpi-block-unit">%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CarbonCodes from "@/submodules/carbon-plugin/codes/carbonCodes";
import { formatEmissionValue, getDisplayUnitLabel, toDisplayValue } from "@/submodules/carbon-plugin/utils/formatters";

export default {
    name: "DashboardKpiRow",
    props: {
        summary: { type: Object, required: true },
        viewMode: { type: String, default: "YEAR", validator: (v) => ["YEAR", "MONTH"].includes(v) },
        selectedMonth: { type: String, default: null },
        yearOverYear: { type: Object, default: () => ({ changeRate: null, direction: "none" }) },
        target: { type: Object, default: null }, // RAW.target widget — needed for col 3 + forecast block
        prevTrend: { type: Array, default: null }, // [{ m, t }] x12 — for 잔여 전년 비교
        displayUnit: { type: String, default: "kgCO2e", validator: (v) => ["kgCO2e", "tCO2e"].includes(v) },
        cutoffDate: { type: String, default: null }, // "YYYY-MM-DD"
    },
    computed: {
        isMonthView() {
            return this.viewMode === "MONTH";
        },
        periodTitle() {
            if (this.isMonthView) {
                return `${this.yearText}년 ${this.cutoffMonth}월 ${this.ytdLabel}`;
            }
            return `${this.yearText}년 ${this.ytdLabel}`;
        },
        primaryCompareLabel() {
            return this.isMonthView ? "전월 대비" : "전년 대비";
        },
        forecastTitle() {
            return this.isMonthView ? "월말 예상" : "연말 예상";
        },
        apiUnit() {
            return this.summary?.unit || "kgCO2e";
        },
        unitLabel() {
            return getDisplayUnitLabel(this.apiUnit, this.displayUnit);
        },
        unitShort() {
            return this.displayUnit === "tCO2e" ? "t" : "kg";
        },

        // cutoff parsing
        cutoffParts() {
            const d = this.cutoffDate || new Date().toISOString().slice(0, 10);
            const [y, m, day] = d.split("-").map(Number);
            return { y, m, d: day };
        },
        yearText() {
            return this.cutoffParts.y;
        },
        cutoffMonth() {
            return this.cutoffParts.m;
        },
        cutoffDay() {
            return this.cutoffParts.d;
        },
        cutoffLabel() {
            const { y, m, d } = this.cutoffParts;
            return `${y}년 ${m}월 ${d}일`;
        },
        ytdLabel() {
            if (this.isMonthView) return "실적";
            if (this.cutoffMonth >= 12 && this.cutoffDay >= 28) return "연간 실적";
            return "누계";
        },
        yearProgressPct() {
            const start = new Date(`${this.cutoffParts.y}-01-01`);
            const cur = new Date(
                `${this.cutoffParts.y}-${String(this.cutoffParts.m).padStart(2, "0")}-${String(this.cutoffParts.d).padStart(2, "0")}`,
            );
            return Math.round((((cur - start) / 86400000 + 1) / 365) * 100);
        },

        // YoY data
        yoyChangeRate() {
            return this.yearOverYear?.changeRate;
        },
        currentYtd() {
            return this.yearOverYear?.currentYtd ?? this.summary?.totalEmission ?? 0;
        },
        prevYtd() {
            return this.yearOverYear?.previousYtd ?? 0;
        },

        forecastValue() {
            return this.numberOrNull(this.target?.forecastValue);
        },
        forecastChangeRate() {
            if (this.isMonthView) {
                return this.yearOverYear?.monthOverMonth?.changeRate ?? null;
            }
            return this.yearOverYear?.forecastVsPrevFullYear?.changeRate ?? null;
        },
        forecastCompareValue() {
            if (this.isMonthView) {
                return this.yearOverYear?.monthOverMonth?.previousValue ?? null;
            }
            return this.yearOverYear?.forecastVsPrevFullYear?.previousFullYear ?? null;
        },
        forecastDeltaHint() {
            return this.isMonthView ? "전월" : "전년";
        },

        goalAmount() {
            return this.numberOrNull(this.target?.goalAmount ?? this.target?.targetEmissions) ?? 0;
        },
        targetReductionRate() {
            return this.numberOrNull(this.target?.targetReductionRate);
        },
        baselineReductionRate() {
            const apiRate = this.numberOrNull(this.target?.reductionRate);
            if (apiRate != null) return apiRate;
            if (this.baselineEmission == null || this.baselineEmission <= 0 || this.baselineCompareEmission == null) {
                return null;
            }
            return ((this.baselineEmission - this.baselineCompareEmission) / this.baselineEmission) * 100;
        },
        targetAchievementReductionRate() {
            if (this.baselineEmission == null || this.baselineEmission <= 0 || this.targetCompareValue == null) {
                return this.baselineReductionRate;
            }
            return ((this.baselineEmission - this.targetCompareValue) / this.baselineEmission) * 100;
        },
        reductionGapPp() {
            if (this.targetAchievementReductionRate == null || this.targetReductionRate == null) return null;
            return this.targetAchievementReductionRate - this.targetReductionRate;
        },
        /** 감축 목표 달성률 = 비교 배출량 기준 감축률 / 목표 감축률. 양수 감축률이 클수록 좋다. */
        targetAchieveRate() {
            if (
                this.targetReductionRate == null ||
                this.targetReductionRate <= 0 ||
                this.targetAchievementReductionRate == null
            ) {
                return null;
            }
            return Math.max(0, (this.targetAchievementReductionRate / this.targetReductionRate) * 100);
        },
        targetAchieveGood() {
            return this.targetAchieveRate == null || this.targetAchieveRate >= 100;
        },

        targetCompareValue() {
            return (
                this.forecastValue ??
                this.numberOrNull(this.target?.operationalValue) ??
                this.numberOrNull(this.currentYtd)
            );
        },
        targetCompareLabel() {
            if (this.forecastValue != null) return this.forecastTitle;
            return this.isMonthView ? "당월 실적" : this.ytdLabel;
        },
        targetGap() {
            if (!this.goalAmount || this.targetCompareValue == null) return 0;
            return this.targetCompareValue - this.goalAmount;
        },
        targetGapRate() {
            if (!this.goalAmount || this.targetCompareValue == null) return null;
            return ((this.goalAmount - this.targetCompareValue) / this.goalAmount) * 100;
        },
        targetWithinGoal() {
            return (this.targetGapRate ?? 0) >= 0;
        },
        baselineEmission() {
            return this.numberOrNull(this.target?.baseEmissions);
        },
        baselineCompareEmission() {
            return (
                this.numberOrNull(this.target?.currentEmissions) ??
                this.numberOrNull(this.target?.operationalValue) ??
                this.numberOrNull(this.target?.actualEmissions) ??
                this.numberOrNull(this.currentYtd)
            );
        },
        baselineEmissionDelta() {
            if (this.baselineEmission == null || this.baselineCompareEmission == null) return null;
            return this.baselineEmission - this.baselineCompareEmission;
        },
        baselineReduced() {
            return this.baselineReductionRate != null && this.baselineReductionRate >= 0;
        },
        baselineTrendLabel() {
            if (this.baselineReductionRate == null) return "데이터 부족";
            if (this.baselineReductionRate > 0) return "감축";
            if (this.baselineReductionRate < 0) return "증가";
            return "변화 없음";
        },

        complianceFlex() {
            return this.summary?.complianceFlex || this.target?.complianceFlex || null;
        },
        complianceDetailFlex() {
            return this.target?.complianceFlex || this.summary?.complianceFlex || null;
        },
        hasComplianceFlex() {
            return this.complianceFlex != null;
        },
        complianceLinked() {
            return this.complianceFlex?.linked !== false;
        },
        complianceTitle() {
            return this.isMonthView ? "월간 정산 기준" : "이행 정산 배출량";
        },
        complianceGrossValue() {
            return this.numberOrNull(this.complianceFlex?.grossActualKg ?? this.complianceDetailFlex?.grossActualKg);
        },
        complianceGrossText() {
            return this.complianceGrossValue == null ? "미확정" : this.formatN(this.complianceGrossValue, 0);
        },
        complianceStatusBadge() {
            return this.complianceStatusMeta(this.complianceFlex?.complianceStatus);
        },
        complianceStatBadge() {
            return this.complianceStatMeta(this.complianceFlex?.complianceStat);
        },
        complianceCapValue() {
            const flex = this.complianceDetailFlex;
            return this.numberOrNull(flex?.effectiveCapKg ?? flex?.assignedCapKg ?? flex?.nominalCapKg);
        },
        complianceCapGap() {
            if (this.complianceCapValue == null || this.complianceGrossValue == null) return null;
            return this.complianceCapValue - this.complianceGrossValue;
        },
        complianceCapGapGood() {
            return (this.complianceCapGap ?? 0) >= 0;
        },
        settlementEntries() {
            const flex = this.complianceDetailFlex;
            if (!flex) return [];
            return [
                { key: "carry", label: "이월", value: this.numberOrNull(flex.carryInAppliedKg) },
                { key: "borrow", label: "차입", value: this.numberOrNull(flex.borrowAppliedKg) },
                { key: "offset", label: "상쇄", value: this.numberOrNull(flex.offsetAppliedKg) },
            ].filter((entry) => entry.value != null);
        },
        totalAdjustment() {
            return this.settlementEntries.reduce((sum, e) => sum + (e.value || 0), 0);
        },
        netSettlementValue() {
            if (this.complianceGrossValue == null) return null;
            return this.complianceGrossValue - this.totalAdjustment;
        },
        grossCapPct() {
            if (!this.complianceCapValue || !this.complianceGrossValue) return 0;
            return Math.min(100, (this.complianceGrossValue / this.complianceCapValue) * 100);
        },
        netCapPct() {
            if (!this.complianceCapValue || this.netSettlementValue == null) return 0;
            return Math.min(100, (this.netSettlementValue / this.complianceCapValue) * 100);
        },
        complianceMessage() {
            const flex = this.complianceFlex;
            if (!flex) return "";
            if (flex.message) return flex.message;
            if (flex.linked === false) return "정산 연결 없음";
            if (flex.periodStatPending || this.complianceGrossValue == null)
                return "실적 확정 후 정산 수치가 표시됩니다.";
            return "";
        },

        overshoot() {
            if (!this.target) return 0;
            const forecast = this.target.forecastValue;
            const goal = this.goalAmount;
            if (forecast == null || !goal) return 0;
            return forecast - goal;
        },
        goalStatBadge() {
            return this.goalStatMeta(this.target?.goalStat);
        },
        // Scope strip data
        scopes() {
            const b = this.summary?.scopeBreakdown || {};
            const scopeLabel = (code, fallback) =>
                CarbonCodes.scopeType.getLabelEn(code) || CarbonCodes.scopeType.getLabel(code) || fallback;
            return [
                { key: "s1", label: scopeLabel("SCOPE1", "Scope 1"), val: b.scope1 || 0, color: "var(--dash-scope1)" },
                { key: "s2", label: scopeLabel("SCOPE2", "Scope 2"), val: b.scope2 || 0, color: "var(--dash-scope2)" },
                { key: "s3", label: scopeLabel("SCOPE3", "Scope 3"), val: b.scope3 || 0, color: "var(--dash-scope3)" },
            ];
        },
    },
    methods: {
        neg(v, ifNeg, ifPos) {
            return v < 0 ? ifNeg : ifPos;
        },
        numberOrNull(v) {
            if (v == null || v === "") return null;
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        },
        signedNumber(v, d = 1) {
            if (v == null || isNaN(v)) return "—";
            const n = Number(v);
            return `${n > 0 ? "+" : ""}${n.toFixed(d)}`;
        },
        formatN(v, d = 0) {
            if (v == null || isNaN(v)) return "—";
            const n = toDisplayValue(Number(v), this.apiUnit, this.displayUnit);
            return Number(n).toLocaleString("ko-KR", { minimumFractionDigits: d, maximumFractionDigits: d });
        },
        // expose existing util in case parent wants to use it
        formatEmissionText(value) {
            return formatEmissionValue(value, this.apiUnit, this.displayUnit, 2);
        },
        pct(num, den) {
            if (!den) return 0;
            return Math.max(0, Math.min(100, (num / den) * 100));
        },
        codeBadgeMeta(codeHelper, value, { warnValues = [], emptyLabel = "—" } = {}) {
            if (!value) return { label: emptyLabel, warn: false };
            const label = codeHelper.getLabel(value);
            return {
                label: label && label !== "-" ? label : value,
                warn: warnValues.includes(value),
            };
        },
        goalStatMeta(stat) {
            return this.codeBadgeMeta(CarbonCodes.goalStat, stat, {
                warnValues: ["FAILED", "ABANDONED"],
            });
        },
        complianceStatusMeta(status) {
            if (!status) return { label: "정산", warn: false };
            return this.codeBadgeMeta(CarbonCodes.complianceStatus, status, {
                warnValues: ["DEFICIT"],
            });
        },
        complianceStatMeta(stat) {
            if (!stat) return { label: "상태 없음", warn: false };
            return this.codeBadgeMeta(CarbonCodes.complianceStat, stat, {
                warnValues: ["PENDING", "DRAFT"],
            });
        },
    },
};
</script>

<style scoped>
.kpi-hero {
    background: var(--dash-surface);
    border: 1px solid var(--dash-hairline-soft);
    border-radius: 14px;
    padding: 14px 20px 12px;
    display: grid;
    grid-template-columns: 1.2fr 1fr 1fr;
    color: var(--dash-ink);
}
.num {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
}

.kpi-col {
    padding: 0 20px;
    position: relative;
}
.kpi-col--first {
    padding-left: 0;
}
.kpi-col--last {
    padding-right: 0;
    display: flex;
    flex-direction: column;
}
.kpi-divider {
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: 0;
    width: 1px;
    background: var(--dash-hairline-soft);
}
.kpi-col--last .kpi-divider {
    display: none;
}

.kpi-eyebrow {
    font-size: 12px;
    letter-spacing: 0.02em;
    color: var(--dash-ink-mute);
    font-weight: 600;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 10px;
}
.kpi-eyebrow--wrap {
    flex-wrap: wrap;
    gap: 6px;
}
.kpi-eyebrow-dash {
    width: 12px;
    height: 1px;
    background: var(--dash-ink-faint);
}
.kpi-eyebrow-faint {
    color: var(--dash-ink-faint);
    letter-spacing: 0.06em;
}

.kpi-big-num {
    font-size: 50px;
    line-height: 1;
    color: var(--dash-ink);
    font-weight: 400;
    letter-spacing: -0.04em;
    display: flex;
    align-items: baseline;
    gap: 10px;
}
.kpi-unit {
    font-size: 18px;
    color: var(--dash-ink-soft);
    font-weight: 500;
    letter-spacing: 0;
}

.kpi-caption {
    font-size: 13px;
    color: var(--dash-ink-soft);
    margin-top: 8px;
    line-height: 1.5;
    max-width: 360px;
}
.kpi-emph {
    font-weight: 700;
}
.kpi-emph--good {
    color: var(--dash-accent);
}
.kpi-emph--bad {
    color: var(--dash-warn);
}

/* year progress */
.kpi-yearbar {
    margin-top: 10px;
}
.kpi-yearbar-labels {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: var(--dash-ink-faint);
    margin-bottom: 6px;
    letter-spacing: 0.06em;
}
.kpi-yearbar-current {
    color: var(--dash-accent);
    font-weight: 600;
}
.kpi-yearbar-track {
    position: relative;
    height: 4px;
    background: var(--dash-hairline-soft);
    border-radius: 999px;
}
.kpi-yearbar-fill {
    position: absolute;
    left: 0;
    top: 0;
    height: 4px;
    background: var(--dash-ink);
    border-radius: 999px;
}
.kpi-yearbar-marker {
    position: absolute;
    top: -3px;
    width: 2px;
    height: 10px;
    background: var(--dash-accent);
    transform: translateX(-1px);
}

/* peer block */
.kpi-block + .kpi-block {
    margin-top: 8px;
}
.kpi-col--last .kpi-block {
    display: flex;
    flex-wrap: wrap;
    align-items: self-end;
    align-content: flex-end;
}
.kpi-col--last .kpi-eyebrow {
    flex: 0 0 100%;
}
.kpi-block-row {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}
.kpi-block-row--target {
    flex: 0 0 auto;
    align-items: flex-end;
}
.kpi-block-value-wrap {
    display: flex;
    align-items: baseline;
    gap: 8px;
}
.kpi-block-value-wrap--side {
    justify-content: flex-end;
}
.kpi-block-value {
    font-size: 38px;
    font-weight: 400;
    letter-spacing: -0.03em;
    line-height: 1;
    color: var(--dash-ink);
}
.kpi-block-value--warn {
    color: var(--dash-warn);
}
.kpi-block-unit {
    font-size: 13px;
    color: var(--dash-ink-soft);
    font-weight: 500;
}
.kpi-block-side-value {
    margin-left: 6px;
    text-align: right;
    flex-shrink: 0;
}
.kpi-block-side-label {
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}
.kpi-block-delta {
    text-align: right;
}
.kpi-block-delta-value {
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
}
.kpi-arrow {
    font-size: 10px;
}
.kpi-delta--good {
    color: var(--dash-accent);
}
.kpi-delta--bad {
    color: var(--dash-warn);
}
.kpi-block-delta-hint {
    font-size: 10px;
    color: var(--dash-ink-mute);
    margin-top: 3px;
    font-weight: 700;
}
.kpi-block-hint {
    font-size: 11px;
    color: var(--dash-ink-faint);
    margin-top: 8px;
    line-height: 1.4;
}
.kpi-block-hr {
    flex: 0 0 auto;
    height: 1px;
    background: var(--dash-hairline-soft);
    margin: 10px 0;
}
.kpi-change-flow {
    margin-top: 0;
    width: auto;
    flex: 1 1 150px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 7px;
    min-width: 0;
    color: var(--dash-ink-soft);
    font-size: 11px;
    line-height: 1.35;
    flex-wrap: wrap;
    font-weight: 600;
}
.kpi-flow-node {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    min-width: 0;
}
.kpi-flow-label {
    color: var(--dash-ink-faint);
    font-weight: 500;
}
.kpi-flow-arrow {
    flex-shrink: 0;
    align-self: center;
    width: 18px;
    height: 1px;
    background: var(--dash-hairline);
    position: relative;
    display: inline-block;
}
.kpi-flow-arrow::after {
    content: "";
    position: absolute;
    right: -1px;
    top: 50%;
    width: 4px;
    height: 4px;
    border-top: 1.5px solid var(--dash-hairline);
    border-right: 1.5px solid var(--dash-hairline);
    transform: translateY(-50%) rotate(45deg);
}
.kpi-flow-unit {
    font-size: 9px;
    color: var(--dash-ink-faint);
    margin-left: 1px;
    align-self: baseline;
}
.kpi-flow-rate {
    font-weight: 700;
}

/* status pill */
.kpi-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    background: var(--dash-accent-wash);
    color: var(--dash-accent);
}
.kpi-pill--warn {
    background: var(--dash-warn-wash);
    color: var(--dash-warn);
}
.kpi-pill--mute {
    background: var(--dash-surface-2);
    color: var(--dash-ink-mute);
}

.kpi-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    display: inline-block;
    flex-shrink: 0;
}

/* Scope 인라인 시각화 */
.ksi-yoy {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    margin: 8px 0 10px;
}
.ksi-yoy-label {
    color: var(--dash-ink-mute);
}
.ksi-yoy-val {
    font-weight: 700;
    font-size: 14px;
}
.ksi-yoy-abs {
    color: var(--dash-ink-faint);
}

.ksi-wrap {
    margin: 0 0 10px;
}
.ksi-bar {
    display: flex;
    height: 6px;
    border-radius: 999px;
    overflow: hidden;
    background: var(--dash-hairline-soft);
    margin-bottom: 8px;
}
.ksi-seg {
    min-width: 2px;
    transition: flex 0.4s ease;
}
.ksi-items {
    display: flex;
    align-items: center;
    gap: 14px;
}
.ksi-item {
    display: flex;
    align-items: center;
    gap: 5px;
}
.ksi-name {
    font-size: 10px;
    font-weight: 600;
    color: var(--dash-ink-mute);
}
.kpi-pill--draft {
    background: var(--dash-warn-wash);
    color: var(--dash-warn);
}

.kpi-flex-strip {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    margin-top: 10px;
}
.kpi-flex-mini {
    min-width: 0;
    border-top: 1px solid var(--dash-hairline-soft);
    padding-top: 6px;
}
.kpi-flex-mini-label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: var(--dash-ink-faint);
    line-height: 1;
}
.kpi-flex-mini-value {
    display: block;
    margin-top: 4px;
    font-size: 13px;
    font-weight: 700;
    color: var(--dash-ink-soft);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.kpi-compliance-note {
    margin-top: 8px;
    font-size: 11px;
    line-height: 1.35;
    color: var(--dash-ink-mute);
}
.ksi-val {
    font-size: 14px;
    font-weight: 600;
    color: var(--dash-ink);
}

.ksi-unit-tag {
    font-size: 10px;
    color: var(--dash-ink-faint);
    margin-left: auto;
}

/* ── Settlement pipeline (좌 → 우 흐름) ────────────────────────── */
.cpl-flow {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    row-gap: 6px;
    justify-content: flex-end;
}

/* 노드 (총 배출량 / 정산 배출량) */
.cpl-node {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 7px 10px;
    border-radius: 9px;
    flex-shrink: 0;
}
.cpl-node--gross {
    background: var(--dash-surface-2);
}
.cpl-node--net {
    background: var(--dash-accent-wash);
}

.cpl-node-lbl {
    font-size: 12px;
    font-weight: 500;
    color: var(--dash-ink-mute);
    letter-spacing: 0.03em;
    white-space: nowrap;
}
.cpl-node-row {
    display: flex;
    align-items: baseline;
    gap: 3px;
}
.cpl-node-val {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.1;
    color: var(--dash-ink);
}
.cpl-node--net .cpl-node-val {
    color: var(--dash-accent);
}
.cpl-node-unit {
    font-size: 14px;
    color: var(--dash-ink-faint);
}

/* 화살표 커넥터 */
.cpl-arrow {
    flex-shrink: 0;
    align-self: center;
    width: 16px;
    height: 1px;
    background: var(--dash-hairline);
    position: relative;
}
.cpl-arrow::after {
    content: "";
    position: absolute;
    right: -1px;
    top: 50%;
    width: 5px;
    height: 5px;
    border-top: 1.5px solid var(--dash-hairline);
    border-right: 1.5px solid var(--dash-hairline);
    transform: translateY(-50%) rotate(45deg);
}

/* 조정 항목 묶음 */
.cpl-adjs {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex-shrink: 0;
}
.cpl-adj {
    display: flex;
    align-items: center;
    gap: 5px;
}
.cpl-adj-amount {
    display: flex;
    align-items: baseline;
    gap: 3px;
    margin-left: auto;
}
.cpl-adj-tag {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.02em;
    padding: 1px 5px;
    border-radius: 3px;
    background: var(--dash-accent-wash);
    color: var(--dash-accent);
    white-space: nowrap;
}
.cpl-adj-val {
    font-size: 11px;
    font-weight: 700;
    color: var(--dash-accent);
    letter-spacing: -0.01em;
}
.cpl-adj-unit {
    font-size: 10px;
    color: var(--dash-ink-faint);
}

/* CAP 결과 (우측 밀착) */
.cpl-cap-result {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    flex-shrink: 0;
}
.cpl-gap-badge {
    font-size: 12px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 999px;
    white-space: nowrap;
}
.cpl-gap-badge--good {
    background: var(--dash-accent-wash);
    color: var(--dash-accent);
}
.cpl-gap-badge--bad {
    background: var(--dash-warn-wash);
    color: var(--dash-warn);
}
.cpl-cap-hint {
    font-size: 9px;
    color: var(--dash-ink-faint);
}
</style>
